import Slider from "@mui/material/Slider";

type Mark = {
	value: number;
	label?: React.ReactNode;
};

type Props = {
	maxValue: number;
	handleChange: (value: number) => void;
	value: number;
	step: number | null;
	disabled?: boolean;
	minValue?: number;
	testID?: string;
	marks?: Mark[] | boolean;
	highlightValue?: number;
	color?: string;
	disabledOpacity?: number;
	pulseCount?: number | "infinite";
};

const CustomSlider = ({
	maxValue,
	handleChange,
	value,
	step,
	disabled,
	minValue = 0,
	testID = "custom-slider",
	marks,
	highlightValue,
	color,
	disabledOpacity = 0.55,
	pulseCount = "infinite",
}: Props) => {
	return (
		<Slider
			sx={{
				cursor: "grab",
				...(color ? { color } : {}),
				"@keyframes suggestedPulse": {
					"0%": {
						boxShadow: "0 0 0 0 currentColor",
						transform: "translate(-50%, -50%) scale(1)",
					},
					"60%": {
						boxShadow: "0 0 0 8px rgba(0,0,0,0)",
						transform: "translate(-50%, -50%) scale(1.15)",
					},
					"100%": {
						boxShadow: "0 0 0 0 rgba(0,0,0,0)",
						transform: "translate(-50%, -50%) scale(1)",
					},
				},
				"& .MuiSlider-thumb": {
					width: 14,
					height: 14,
					transition: "opacity 200ms ease",
				},
				"&.Mui-disabled": {
					...(color ? { color } : {}),
					opacity: disabledOpacity,
				},
				"&.Mui-disabled .MuiSlider-thumb": {
					opacity: 0,
					pointerEvents: "none",
				},
				"&.Mui-disabled .MuiSlider-rail": {
					opacity: 0.4,
				},
				"&.Mui-disabled .MuiSlider-track": {
					opacity: 1,
					backgroundColor: "currentColor",
					borderColor: "currentColor",
				},
				"&.Mui-disabled .MuiSlider-mark": {
					backgroundColor: "currentColor",
				},
				"&.Mui-disabled .MuiSlider-markActive": {
					backgroundColor: "currentColor",
				},
				"& .MuiSlider-rail": {
					height: 4,
				},
				"& .MuiSlider-track": {
					height: 4,
				},
				...(marks
					? {
							"& .MuiSlider-mark": {
								height: 10,
								width: 10,
								borderRadius: 5,
								backgroundColor: "currentColor",
								opacity: 0.4,
								transform: "translate(-50%, -50%)",
								transition: "opacity 200ms ease, transform 200ms ease",
							},
							"& .MuiSlider-markActive": {
								opacity: 1,
								backgroundColor: "currentColor",
							},
							...(highlightValue != null && Array.isArray(marks)
								? {
										[`& .MuiSlider-mark[data-index="${marks.findIndex(
											(m) => m.value === highlightValue,
										)}"]`]: {
											backgroundColor: "currentColor",
											opacity: 1,
											width: 14,
											height: 14,
											borderRadius: 7,
											zIndex: 2,
											animation: `suggestedPulse 1.6s ease-out ${pulseCount}`,
										},
									}
								: {}),
							"& .MuiSlider-markLabel": {
								fontSize: 12,
								whiteSpace: "normal",
								textAlign: "center",
								maxWidth: 110,
								lineHeight: 1.2,
								top: 22,
							},
							...(Array.isArray(marks) && marks.length > 1
								? {
										"& .MuiSlider-markLabel[data-index='0']": {
											transform: "translateX(0%)",
											textAlign: "left",
										},
										[`& .MuiSlider-markLabel[data-index='${marks.length - 1}']`]:
											{
												transform: "translateX(-100%)",
												textAlign: "right",
											},
									}
								: {}),
						}
					: {}),
			}}
			min={minValue}
			max={maxValue}
			onChange={(_e, value) => handleChange(value as number)}
			step={step}
			disabled={disabled}
			value={value}
			// biome-ignore lint/suspicious/noExplicitAny: marks parameter for MUI Slider
			marks={marks as any}
			data-testid={testID}
		/>
	);
};

export default CustomSlider;
